/**
 * SVG renderers for the freehand line drawings (horizontal / vertical /
 * diagonal), extracted from Chart.tsx. Each is interactive — it wires
 * select / drag / delete — so it takes a `LineRenderCtx` carrying the live
 * component handles (series, coordinate converter, theme, current selection,
 * the drag/menu starters, dispatch). Bodies are verbatim moves; only the
 * closed-over references became `ctx.*`.
 */
import type { MouseEvent, ReactNode, MouseEvent as RMouseEvent } from "react";
import type { AppDispatch } from "@/state/store";
import { fmtLineLabel } from "@/utils/chartTime";
import {
  setSelectedDrawing,
  type ChartTheme,
  type SelectedDrawing,
  type DiagonalLine,
  type HorizontalLine,
  type VerticalLine,
} from "@/state/slices/uiSlice";
import { svgDasharrayFromName } from "./priceLineStyles";
import { drawingMenuItems } from "./drawingMenu";
import type { AnySeries } from "./chartData";
import type { DragTarget, MenuItem } from "./types";
import styles from "./Chart.module.css";

/** When a drawing is locked: select-only handler (no drag). Does NOT stop
 *  propagation so the event reaches LWC and the chart can still pan. The
 *  container deselect listener skips elements with data-drawing-hit. */
function lockedMouseDown(
  ctx: LineRenderCtx,
  kind: "hline" | "vline" | "diagonal",
  id: string,
) {
  return (e: RMouseEvent) => {
    if (e.button !== 0) return;
    ctx.startLockedPan(e.nativeEvent);
    ctx.dispatch(setSelectedDrawing({ tabId: ctx.tabId, selected: { kind, id } }));
  };
}

export interface LineRenderCtx {
  series: AnySeries | null;
  timeToX: (time: number) => number | null;
  theme: ChartTheme;
  selectedDrawing: SelectedDrawing | null;
  tabId: string;
  startDrag: (
    target: Exclude<DragTarget, { type: "trading" } | { type: "draft" }>,
  ) => (e: MouseEvent) => void;
  startLockedPan: (nativeEvent: globalThis.MouseEvent) => void;
  showMenu: (items: MenuItem[]) => (e: MouseEvent) => void;
  dispatch: AppDispatch;
  digits: number;
  /** Plot-area width in px (excludes the right price scale) — used to
   *  anchor right-aligned text labels just inside the price axis. */
  plotWidth: number;
}

export function renderHorizontalLine(
  line: HorizontalLine,
  ctx: LineRenderCtx,
): ReactNode {
  const series = ctx.series;
  if (!series) return null;
  if (line.visible === false) return null;
  const { id, price } = line;
  const y = series.priceToCoordinate(price);
  if (y == null) return null;
  const isSelected =
    ctx.selectedDrawing?.kind === "hline" && ctx.selectedDrawing.id === id;
  const color = line.color ?? ctx.theme.hlineColor ?? "#2962FF";
  const w = line.width ?? ctx.theme.hlineWidth ?? 1;
  const dash = svgDasharrayFromName(line.style ?? ctx.theme.hlineStyle);
  return (
    <g key={id}>
      {/* Wide invisible stroke as the drag hit area. */}
      <line
        x1="0"
        x2="100%"
        y1={y}
        y2={y}
        stroke="transparent"
        strokeWidth={10}
        className={styles.hLineHit}
        data-drawing-hit="true"
        onMouseDown={
          line.locked
            ? lockedMouseDown(ctx, "hline", id)
            : ctx.startDrag({ type: "hline", id })
        }
        onContextMenu={ctx.showMenu(
          drawingMenuItems(ctx.dispatch, ctx.tabId, "hline", id),
        )}
      />
      <line
        x1="0"
        x2="100%"
        y1={y}
        y2={y}
        stroke={color}
        strokeWidth={isSelected ? w + 1 : w}
        strokeDasharray={dash}
      />
      {/* Price tag on the RIGHT axis — styled like the native price-line /
          MA axis label: an OPAQUE filled badge with white text, so it covers
          the price-scale numbers at that price instead of overlapping them
          (thin text over the axis was the readability problem). Anchored to
          the chart's right edge via x="100%" + a translate of the badge width
          so it sits on the price scale. */}
      {(() => {
        const label = price.toFixed(ctx.digits);
        const badgeW = label.length * 7 + 10;
        const badgeH = 16;
        return (
          <g transform={`translate(-${badgeW} 0)`}>
            <rect
              x="100%"
              y={y - badgeH / 2}
              width={badgeW}
              height={badgeH}
              rx={2}
              fill={color}
            />
            <text
              x="100%"
              dx={badgeW / 2}
              y={y + 4}
              textAnchor="middle"
              fill="#fff"
              fontSize={11}
            >
              {label}
            </text>
          </g>
        );
      })()}
      {/* Optional free-form text label. Placement + colour resolve per-line
          override → tool-level default (theme) → hardcoded. Left anchors to
          the chart's left edge; right anchors just inside the price scale
          (plotWidth). Above/below offsets clear the line so the text never
          sits on the stroke. */}
      {line.text && (() => {
        const hpos = line.textHPos ?? ctx.theme.hlineTextHPos ?? "left";
        const vpos = line.textVPos ?? ctx.theme.hlineTextVPos ?? "above";
        const textColor = line.textColor ?? ctx.theme.hlineTextColor ?? color;
        const pad = 6;
        const tx = hpos === "left" ? pad : Math.max(pad, ctx.plotWidth - pad);
        const anchor = hpos === "left" ? "start" : "end";
        const ty = vpos === "above" ? y - 6 : y + 15;
        return (
          <text
            x={tx}
            y={ty}
            textAnchor={anchor}
            fill={textColor}
            fontSize={11}
            style={{ pointerEvents: "none" }}
          >
            {line.text}
          </text>
        );
      })()}
    </g>
  );
}

export function renderVerticalLine(
  line: VerticalLine,
  ctx: LineRenderCtx,
): ReactNode {
  const { id, time } = line;
  if (line.visible === false) return null;
  const x = ctx.timeToX(time);
  if (x == null) return null;
  // UTC — matches the chart axis (chartTime.DISPLAY_TZ). See issue #90.
  const label = fmtLineLabel(time);
  const isSelected =
    ctx.selectedDrawing?.kind === "vline" && ctx.selectedDrawing.id === id;
  const color = line.color ?? ctx.theme.vlineColor ?? "#FF9800";
  const w = line.width ?? ctx.theme.vlineWidth ?? 1;
  const dash = svgDasharrayFromName(line.style ?? ctx.theme.vlineStyle);
  return (
    <g key={id}>
      <line
        x1={x}
        x2={x}
        y1="0"
        y2="100%"
        stroke="transparent"
        strokeWidth={10}
        className={styles.vLineHit}
        data-drawing-hit="true"
        onMouseDown={
          line.locked
            ? lockedMouseDown(ctx, "vline", id)
            : ctx.startDrag({ type: "vline", id })
        }
        onContextMenu={ctx.showMenu(
          drawingMenuItems(ctx.dispatch, ctx.tabId, "vline", id),
        )}
      />
      <line
        x1={x}
        x2={x}
        y1="0"
        y2="100%"
        stroke={color}
        strokeWidth={isSelected ? w + 1 : w}
        strokeDasharray={dash}
      />
      <text x={x} y={14} dx={4} fill={color} fontSize={11}>
        {label}
      </text>
    </g>
  );
}

export function renderDiagonalLine(
  line: DiagonalLine,
  opacity: number,
  key: string,
  ctx: LineRenderCtx,
): ReactNode {
  const series = ctx.series;
  if (!series) return null;
  if (line.visible === false) return null;
  const startX = ctx.timeToX(line.startTime);
  const endX = ctx.timeToX(line.endTime);
  const startY = series.priceToCoordinate(line.startPrice);
  const endY = series.priceToCoordinate(line.endPrice);
  if (startX == null || endX == null || startY == null || endY == null)
    return null;
  const isPreview = key.startsWith("preview");
  const isSelected =
    !isPreview &&
    ctx.selectedDrawing?.kind === "diagonal" &&
    ctx.selectedDrawing.id === line.id;
  // Per-instance overrides fall back to themed color/width/style.
  const stroke = line.color ?? ctx.theme.trendColor ?? "#2962FF";
  const w = line.width ?? ctx.theme.trendWidth ?? 1;
  const dash = svgDasharrayFromName(line.style ?? ctx.theme.trendStyle);
  return (
    <g key={key} opacity={opacity}>
      {/* Wide invisible stroke as the drag-along-line hit area. Only mounted
          when not in preview so the placement gesture doesn't trigger
          selection on its own preview. */}
      {!isPreview && (
        <line
          x1={startX}
          y1={startY}
          x2={endX}
          y2={endY}
          stroke="transparent"
          strokeWidth={10}
          className={styles.hLineHit}
          data-drawing-hit="true"
          onMouseDown={(e) => {
            // Clicking the line body just selects — drag-to-move is handled
            // by the two endpoint circles. When not locked, stop propagation
            // to prevent the container deselect. When locked, let the event
            // bubble so LWC can pan; the deselect is skipped via data-drawing-hit.
            if (!line.locked) e.stopPropagation();
            ctx.dispatch(
              setSelectedDrawing({
                tabId: ctx.tabId,
                selected: { kind: "diagonal", id: line.id },
              }),
            );
          }}
          onContextMenu={ctx.showMenu(
            drawingMenuItems(ctx.dispatch, ctx.tabId, "diagonal", line.id),
          )}
        />
      )}
      <line
        x1={startX}
        y1={startY}
        x2={endX}
        y2={endY}
        stroke={stroke}
        strokeWidth={isSelected ? w + 0.6 : w}
        strokeDasharray={dash}
        pointerEvents="none"
      />
      {/* Anchor handles — drag-rearrange. */}
      {!isPreview && (
        <>
          <circle
            cx={startX}
            cy={startY}
            r={isSelected ? 6 : 5}
            fill={isSelected ? "#3b82f6" : "#161922"}
            stroke={isSelected ? "#ffffff" : "#9aa0a6"}
            strokeWidth={1.5}
            className={styles.fibHandle}
            data-drawing-hit="true"
            onMouseDown={
              line.locked
                ? lockedMouseDown(ctx, "diagonal", line.id)
                : ctx.startDrag({ type: "diagonal", id: line.id, anchor: "start" })
            }
            onContextMenu={ctx.showMenu(
              drawingMenuItems(ctx.dispatch, ctx.tabId, "diagonal", line.id),
            )}
          />
          <circle
            cx={endX}
            cy={endY}
            r={isSelected ? 6 : 5}
            fill={isSelected ? "#3b82f6" : "#161922"}
            stroke={isSelected ? "#ffffff" : "#9aa0a6"}
            strokeWidth={1.5}
            className={styles.fibHandle}
            data-drawing-hit="true"
            onMouseDown={
              line.locked
                ? lockedMouseDown(ctx, "diagonal", line.id)
                : ctx.startDrag({ type: "diagonal", id: line.id, anchor: "end" })
            }
            onContextMenu={ctx.showMenu(
              drawingMenuItems(ctx.dispatch, ctx.tabId, "diagonal", line.id),
            )}
          />
        </>
      )}
    </g>
  );
}
